import { useCountry } from "@/lib/country";
import { ChevronDown, Globe, Check } from "lucide-react";
import { useState, useRef, useEffect } from "react";

export default function CountrySelector({ compact = false }) {
  const { countries, country, setCountry, currency } = useCountry();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div ref={ref} className="relative" data-testid="country-selector">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 rounded-full border transition-colors ${country ? "border-[#B47A9A]/60 text-white bg-[#B47A9A]/10" : "border-[#5E3A5C]/40 text-[#F3E9EC]/85"} hover:border-[#B47A9A]/80 ${compact ? "px-2.5 py-1 text-xs" : "px-3.5 py-1.5 text-xs"}`}
        data-testid={compact ? "country-selector-toggle-mobile" : "country-selector-toggle"}
        aria-label="Select your country"
      >
        {country ? <span className="text-base leading-none">{country.flag}</span> : <Globe className="w-3.5 h-3.5"/>}
        <span className="font-medium tracking-wide whitespace-nowrap">
          {country ? (compact ? country.currency : country.name) : (compact ? "Country" : "Your country")}
        </span>
        {!compact && country && <span className="text-[10px] font-mono text-[#B47A9A] pl-1 whitespace-nowrap">· {currency.symbol} {currency.code}</span>}
        <ChevronDown className="w-3 h-3 opacity-70"/>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 max-h-96 overflow-y-auto rounded-2xl glass shadow-2xl z-50" data-testid="country-selector-menu">
          <div className="px-4 py-3 border-b border-[#5E3A5C]/30">
            <p className="text-[11px] font-mono uppercase tracking-widest text-[#B47A9A]">Where are you travelling from?</p>
            <p className="text-xs text-[#F3E9EC]/60 mt-1">We'll tailor treatments, costs and testimonials to you.</p>
          </div>
          <button
            onClick={() => { setCountry(""); setOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm border-b border-[#5E3A5C]/25 ${!country ? "bg-[#B47A9A]/15 text-white" : "text-[#F3E9EC]/70 hover:bg-[#5E3A5C]/25 hover:text-white"}`}
            data-testid="country-option-global"
          >
            <Globe className="w-5 h-5 text-[#B47A9A]"/>
            <div className="flex-1"><div>Global (all countries)</div><div className="text-[10px] text-[#F3E9EC]/45">Default INR pricing</div></div>
            {!country && <Check className="w-4 h-4 text-[#B47A9A]"/>}
          </button>
          {countries.map(c => (
            <button
              key={c.id}
              onClick={() => { setCountry(c.slug); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${country?.slug === c.slug ? "bg-[#B47A9A]/15 text-white" : "text-[#F3E9EC]/80 hover:bg-[#5E3A5C]/25 hover:text-white"}`}
              data-testid={`country-option-${c.slug}`}
            >
              <span className="text-xl leading-none">{c.flag}</span>
              <div className="flex-1"><div>{c.name}</div><div className="text-[10px] font-mono text-[#F3E9EC]/50">{c.currency} · {c.flight_hours}</div></div>
              {country?.slug === c.slug && <Check className="w-4 h-4 text-[#B47A9A]"/>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
